import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useTheme } from "react-jss";
import { GridClass } from "../../../../../../common/enum/GridClass";
import { IManagementUserForm } from "../../../../../../redux/reducers/management/user/form";
import { compileNameOfProperty, convertToLocal, mapIDescriptionToDropDownOptions } from "../../../../../../utils";
import { FormField } from "../../../../../common/fields/fields";
import { ManagementUserDefaultFormStyle } from "../../baseFormStyle.jss";
import { FormatDate } from "../../../../../../common/enum/dateTime/FormatDate";
import { LabelText } from "../../../../../common/labels/labelText/labelText";
import { requestMaxLength } from "../../../../../request/form/create/validation/requestConfig";
import { useEffect } from "react";
import { myProfileResolver } from "../../default/resolver";
import { useAppSelector } from "../../../../../../redux/hooks";
import { useDispatch } from "react-redux";
import { fetchTerminals } from "../../../../../../redux/actions/catalog/catalog";
import { Selector } from "../../../../../common/selectors/selector";

interface IManagementUserTerminalAdminFormDataProps {
    generalUserData: IManagementUserForm;
    setIsValidTab: (value: boolean) => any;
    onSelectorChange: (event: any, option: any, name: string) => any;
    onInputChange: (event: any) => any;
    editMode: boolean;
}

export const ManagementUserTerminalAdminFormData: React.FC<IManagementUserTerminalAdminFormDataProps> = ({
    generalUserData,
    setIsValidTab,
    onSelectorChange,
    onInputChange, 
    editMode
}) => {

    const { t } = useTranslation("common", { keyPrefix: "MANAGEMENT.FORMS.MANAGEMENT_USER" });
    const dispatch = useDispatch();
    const theme = useTheme();
    const style = ManagementUserDefaultFormStyle({ theme });
    const terminals = useAppSelector((store) => store.catalog.terminals.data);
    const dischargedDate = generalUserData.unregistered ? convertToLocal(generalUserData.unregisteredOn, FormatDate.DATE) : "";

    const {
        register,
        formState: { errors: error, isValid },
    } = useForm<IManagementUserForm>({ mode: "onChange", resolver: myProfileResolver });

    useEffect(() => {
        !terminals.length && dispatch(fetchTerminals());
    }, [terminals]);

    useEffect(() => {
        setIsValidTab(generalUserData.terminalId !== undefined && isValid);
    }, [generalUserData.terminalId, isValid]);

    return (
        <div className={`  ${style.containerForm}`}>
            <div
                className={`ms-Grid ${style.gridContainer}`}
                dir="ltr"
            >
                <div className={GridClass.ROW_GRID}>
                    <div className={GridClass.LONG_DOBLE_CELL}>
                        <Selector
                            key={String(generalUserData.terminalId) + t("TYPE")}
                            id={String(generalUserData.terminalId)}
                            title={t("TYPE")}
                            options={mapIDescriptionToDropDownOptions(terminals)}
                            selectedKey={generalUserData.terminalId}
                            name={compileNameOfProperty<IManagementUserForm>("terminalId")}
                            onChange={onSelectorChange}
                            disabled={generalUserData.id ? true : false}
                            required={true}
                        />
                    </div>
                    <div className={GridClass.LONG_DOBLE_CELL}></div>
                </div>
                <div className={GridClass.ROW_GRID}>
                    <div className={GridClass.THREE_CELL}>
                        <FormField
                            type="text"
                            value={generalUserData.name ?? ""}
                            maxLength={requestMaxLength.DESCRIPTION}
                            label={t("NAME")}
                            {...register(compileNameOfProperty<IManagementUserForm>("name"), {
                                onChange: onInputChange,
                                value: generalUserData.name ?? "",
                                required: true,
                            })}
                            error={error.name}
                            isRequired={true}
                        />
                    </div>

                    <div className={GridClass.THREE_CELL}>
                        <FormField
                            type="text"
                            value={generalUserData.surName ?? ""}
                            maxLength={requestMaxLength.DESCRIPTION}
                            label={t("SURNAME")}
                            error={error.surName}
                            {...register(compileNameOfProperty<IManagementUserForm>("surName"), {
                                onChange: onInputChange,
                                value: generalUserData.surName ?? "",
                                required: true,
                            })}
                            isRequired={true}
                        />
                    </div>
                    <div className={GridClass.THREE_CELL}>
                        <FormField
                            type="text"
                            value={generalUserData.lastName ?? ""}
                            maxLength={requestMaxLength.DESCRIPTION}
                            label={t("LASTNAME")}
                            {...register(compileNameOfProperty<IManagementUserForm>("lastName"), {
                                onChange: onInputChange,
                                value: generalUserData.lastName ?? "",
                            })}
                        />
                    </div>
                </div>
                <div className={GridClass.ROW_GRID}>
                    <div className={GridClass.LONG_DOBLE_CELL}>
                        <FormField
                            type="text"
                            value={generalUserData.email ?? ""}
                            maxLength={255}
                            label={t("EMAIL")}
                            {...register(compileNameOfProperty<IManagementUserForm>("email"), {
                                onChange: onInputChange,
                                value: generalUserData.email,
                                required: true,
                            })}
                            error={error.email}
                            disabled={editMode}
                            isRequired={true}
                        />
                    </div>
                    <div className={GridClass.LONG_DOBLE_CELL}>
                        <FormField
                            type="text"
                            value={generalUserData.phoneNumber ?? ""}
                            maxLength={255}
                            label={t("PHONE_NUMBER")}
                            error={error.phoneNumber}
                            {...register(compileNameOfProperty<IManagementUserForm>("phoneNumber"), {
                                onChange: onInputChange,
                                value: generalUserData.phoneNumber,
                                required: false,
                            })}
                        />
                    </div>
                </div>
                {generalUserData.unregistered && (
                    <div className={GridClass.ROW_GRID}>
                        <div className={GridClass.SIMPLE_CELL}>
                            <LabelText
                                key={t("STATE") + generalUserData.unregisteredOn}
                                name={t("STATE")}
                                value={`${t("DISCHARGED")} - ${dischargedDate}`}
                                className={style.discharged}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
